// The shop's policy pages (COMMERCE_BUILD.md §13 ENG, b-policies): "Returns
// policy" (/returns-policy) and "Terms of sale" (/terms-of-sale) are ordinary
// website pages the business edits in the studio. Linked from checkout and the
// footers only once published (the preview shows drafts too). Presentation only.

export const POLICY_PAGES = Object.freeze({
    returns: {name: "returns-policy", label: "Returns policy"},
    terms: {name: "terms-of-sale", label: "Terms of sale"},
});
export const POLICY_PAGE_NAMES = Object.values(POLICY_PAGES).map((p) => p.name);

const text = (v) => (typeof v === "string" ? v.trim() : "");
const safePath = (p) => (typeof p === "string" && /^\/[a-z0-9-]{1,80}$/.test(p) ? p : null);

// website_pages rows -> {returns: {url, title} | null, terms: {url, title} | null}.
// Live: a published title (deleted rows never count). Preview: a draft title counts.
export function policyLinksFrom(rows, {preview = false} = {}) {
    const out = {returns: null, terms: null};
    for (const [kind, p] of Object.entries(POLICY_PAGES)) {
        const r = (Array.isArray(rows) ? rows : []).find((x) => x && x.name === p.name && x.deleted !== true);
        if (!r) continue;
        const title = text(r.title) || (preview && r.has_unpublished_changes ? text(r.unpublished?.title) : "");
        const url = safePath(r.path);
        if (title && url) out[kind] = {url, title};
    }
    return out;
}

// The footer's links, in a fixed order (labels are ours, not the page titles).
export function footerPolicyLinks(policies) {
    return Object.entries(POLICY_PAGES)
        .filter(([kind]) => policies?.[kind]?.url)
        .map(([kind, p]) => ({label: p.label, to: policies[kind].url}));
}

// The same for the classic footer (LegacyFooter takes {label, url}).
export const classicPolicyLinks = (policies) => footerPolicyLinks(policies).map((l) => ({label: l.label, url: l.to}));

// The privacy policy page (a standard website page, model text in bristol database/model_privacy.md). Not one of the
// shop's two policy pages (the footers already link it), but the checkout points to it: the shopper is told where to read
// how the business uses their details when they give them (UK GDPR Art. 13). Published only; the preview shows a draft.
export const PRIVACY_PAGE = Object.freeze({name: "privacy-policy", label: "Privacy policy"});
export function privacyLinkFrom(rows, {preview = false} = {}) {
    const r = (Array.isArray(rows) ? rows : []).find((x) => x && x.name === PRIVACY_PAGE.name && x.deleted !== true);
    if (!r) return null;
    const title = text(r.title) || (preview && r.has_unpublished_changes ? text(r.unpublished?.title) : "");
    const url = safePath(r.path);
    return title && url ? {url, title} : null;
}

// The line under Pay: "By paying you agree to our Terms of sale · Returns policy". Only the returns policy published:
// "Our Returns policy" (nothing to agree to). Nothing published: null (no line). With the privacy policy published the
// note also carries `privacy` ({label, to}): "How we use your details is explained in our Privacy policy."
export function checkoutPolicyNote(policies) {
    const links = [];
    if (policies?.terms?.url) links.push({label: POLICY_PAGES.terms.label, to: policies.terms.url});
    if (policies?.returns?.url) links.push({label: POLICY_PAGES.returns.label, to: policies.returns.url});
    const privacy = policies?.privacy?.url ? {label: PRIVACY_PAGE.label, to: policies.privacy.url} : null;
    if (!links.length && !privacy) return null;
    return {lead: policies?.terms?.url ? "By paying you agree to our" : "Our", links, ...(privacy ? {privacy} : {})};
}
